import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, LogIn } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { Logo } from '@/components/Logo';
import { apiFetch } from '@/lib/api';

type JoinState =
  | { status: 'loading' }
  | { status: 'validating'; gameName?: string }
  | { status: 'not_logged_in'; gameName: string; gameId: string }
  | { status: 'joining'; gameName: string }
  | { status: 'success'; gameName: string; gameId: string }
  | { status: 'error'; message: string };

export function Join() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<JoinState>({ status: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'No invitation token provided' });
      return;
    }

    async function checkAndJoin() {
      const supabase = createClient();

      // First validate the token
      setState({ status: 'validating' });

      const validateResponse = await apiFetch(`/api/games/join?token=${encodeURIComponent(token!)}`);
      const validateData = await validateResponse.json();

      if (!validateResponse.ok) {
        setState({ status: 'error', message: validateData.error || 'Invalid invitation' });
        return;
      }

      // Check if user is logged in
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Not logged in - show login prompt with game info
        setState({
          status: 'not_logged_in',
          gameName: validateData.gameName,
          gameId: validateData.gameId
        });
        return;
      }

      // User is logged in, try to join
      setState({ status: 'joining', gameName: validateData.gameName });

      const joinResponse = await apiFetch('/api/games/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const joinData = await joinResponse.json();

      if (!joinResponse.ok) {
        setState({ status: 'error', message: joinData.error || 'Failed to join game' });
        return;
      }

      setState({
        status: 'success',
        gameName: joinData.gameName || validateData.gameName,
        gameId: joinData.gameId
      });

      // Redirect to games list after a short delay
      setTimeout(() => {
        navigate('/games');
      }, 1500);
    }

    checkAndJoin();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Logo />
        </div>

        {/* Content Card */}
        <div className="bg-neutral-950 border border-neutral-800 rounded-lg p-6">
          {state.status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-neutral-500 mx-auto mb-4" />
              <p className="text-neutral-400">Loading...</p>
            </div>
          )}

          {state.status === 'validating' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-neutral-500 mx-auto mb-4" />
              <p className="text-neutral-400">Validating invitation...</p>
            </div>
          )}

          {state.status === 'not_logged_in' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-white">You&apos;re Invited!</h2>
                <p className="text-neutral-400 mt-2">
                  You&apos;ve been invited to join
                </p>
                <p className="text-white font-medium text-lg mt-1">
                  {state.gameName}
                </p>
              </div>

              <div className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4">
                <p className="text-sm text-neutral-400 text-center">
                  Sign in with your account to join this adventure.
                </p>
              </div>

              <Link
                to={`/login?redirect=${encodeURIComponent(`/join?token=${token}`)}`}
                className="w-full py-2.5 rounded-lg text-sm font-medium bg-white text-black hover:bg-neutral-200 transition-colors flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                Sign in to join
              </Link>
            </div>
          )}

          {state.status === 'joining' && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
              <p className="text-white font-medium">Joining {state.gameName}...</p>
            </div>
          )}

          {state.status === 'success' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Welcome!</h2>
              <p className="text-neutral-400">
                You&apos;ve joined <span className="text-white">{state.gameName}</span>
              </p>
              <p className="text-neutral-500 text-sm mt-2">
                The Game Master will start the adventure soon.
              </p>
              <p className="text-neutral-600 text-xs mt-1">Redirecting to your games...</p>
            </div>
          )}

          {state.status === 'error' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Unable to Join</h2>
              <p className="text-neutral-400 mb-6">{state.message}</p>
              <Link
                to="/games"
                className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 text-white rounded-lg text-sm hover:bg-neutral-700 transition-colors"
              >
                Go to my games
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-neutral-600 text-xs mt-6">
          Every wish casts a shadow.
        </p>
      </div>
    </div>
  );
}
